import axios from '../axiosConfig';

export interface Role {
  roleId?: string;
  name: string;
  description?: string;
  permissions?: string[];
  userCount?: number;
  id?: string;
  createdAt?: string;
  updatedAt?: string | null;
}

export interface UsersApiItem {
  role?: string | null; // Keep for backward compatibility
  roles: Role[];
  businessUnit?: string | null;
  productLine?: string | null;
  department?: string | null;
  userId?: string | null;
  name: string;
  emailAddress: string;
  avatarUri?: string | null;
  id: string;
  createdAt: string;
  updatedAt?: string | null;
}

export interface UsersApiResponse {
  results: UsersApiItem[];
  totalCount: number;
  totalResults?: number;
  activeUsers?: number;
}

export interface UserDetails {
  userId: string | null;
  name: string | null;
  emailAddress: string | null;
  department?: string | null;
  role?: string | null; // Keep for backward compatibility
  roles?: Role[];
  businessUnit?: string | null;
  productLine?: string | null;
  avatarUri?: string | null;
  id: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface UsersQueryParams {
  emailAddresses?: string[];
  name?: string;
  userId?: string;
  textSearch?: string;
  page?: number;
  pageSize?: number;
  skip?: number;
  includeDeleted?: boolean;
  orderByField?: string;
  descending?: boolean;
}

export async function fetchUsers(params?: UsersQueryParams): Promise<UsersApiResponse> {
  const p = params || {};
  const page = p.page ?? 1;
  const pageSize = p.pageSize ?? 25;
  const orderByField = p.orderByField && p.orderByField.trim() ? p.orderByField : 'Name';
  const query: Record<string, any> = {
    EmailAddresses: p.emailAddresses ?? [],
    Name: p.name ?? '',
    UserId: p.userId ?? '',
    TextSearch: p.textSearch ?? '',
    Page: page,
    PageSize: pageSize,
    Skip: p.skip ?? Math.max(0, (page - 1) * pageSize),
    IncludeDeleted: typeof p.includeDeleted === 'boolean' ? p.includeDeleted : false,
    OrderByField: orderByField,
    Descending: typeof p.descending === 'boolean' ? p.descending : false,
  };

  const { data } = await axios.get<UsersApiResponse>(`/Users`, {
    params: query,
    paramsSerializer: {
      serialize: (rawParams: Record<string, any>) => {
        const usp = new URLSearchParams();
        Object.entries(rawParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) => {
              if (item !== undefined && item !== null) {
                usp.append(key, String(item));
              }
            });
            return;
          }

          if (value !== undefined && value !== null) {
            usp.append(key, String(value));
          }
        });
        return usp.toString();
      },
    },
  });
  return data;
}

export async function fetchUserById(id: string): Promise<UserDetails> {
  const { data } = await axios.get<UserDetails>(`/Users/${encodeURIComponent(id)}`);
  return data;
}

export interface CreateUserPayload {
  userId: string | null;
  name: string | null;
  emailAddress: string | null;
  department?: string | null;
  role?: string | null; // Keep for backward compatibility
  roles?: Role[];
  businessUnit?: string | null;
  productLine?: string | null;
  avatarUri?: string | null;
  id?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export async function createUser(payload: CreateUserPayload): Promise<UsersApiItem> {
  const { data } = await axios.post<UsersApiItem>(`/Users`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return data;
}

export interface UpdateUserPayload {
  userId: string | null;
  name: string | null;
  emailAddress: string | null;
  department?: string | null;
  role?: string | null; // Keep for backward compatibility
  roles?: Role[];
  businessUnit?: string | null;
  productLine?: string | null;
  avatarUri?: string | null;
  id: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export async function updateUser(id: string, payload: UpdateUserPayload): Promise<UsersApiItem> {
  const { data } = await axios.put<UsersApiItem>(`/Users/${encodeURIComponent(id)}`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return data;
}

export async function deleteUserById(id: string): Promise<void> {
  await axios.delete(`/Users/${encodeURIComponent(id)}`);
}

// ---- UserInfo (current user + permissions) ----
export interface CurrentUserInfo {
  userId: string;
  name: string;
  emailAddress: string;
  department?: string | null;
  businessUnit?: string | null;
  productLine?: string | null;
  avatarUri?: string | null;
  id: string;
  createdAt: string;
  updatedAt?: string | null;
}

export interface UserInfoResponse {
  user: CurrentUserInfo;
  permissions: string[];
  roles: Role[];
}

export async function fetchUserInfo(accessToken?: string): Promise<UserInfoResponse> {
  const { data } = await axios.get<UserInfoResponse>('/Users/userinfo', accessToken ? {
    headers: { Authorization: `Bearer ${accessToken}` },
  } : undefined);
  // Normalize possible nulls
  const permissions = Array.isArray(data?.permissions) ? data.permissions : [];
  const roles = Array.isArray(data?.roles) ? data.roles : [];
  return { user: data.user, permissions, roles };
}



